import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Building,
  Users,
  Database,
  LogOut,
  GitBranch,
  Clock,
  Play,
  FileText,
  Settings2,
  Activity,
} from 'lucide-react';
import { Sidebar } from '@components/base/navigation';
import { Icon, Text, Badge, Tooltip } from '@components/base/display';
import { Button } from '@components/base/actions';
import { HStack, VStack } from '@components/base/layout';
import { TenantSpoofDropdown } from '@/shared/spoof/TenantSpoofDropdown';
import { useAdminAuthStore } from '../auth/store';
import type { AdminUser } from '../auth/types';
import { adminApiClient } from '../api/adminApiClient';

export function AdminSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAdminAuthStore((state) => state.user);
  const logout = useAdminAuthStore((state) => state.logout);
  const activeTenantId = useAdminAuthStore((state) => state.activeTenantId);

  const spoofedTenantId = useAdminAuthStore((s) => s.spoofedTenantId);
  const spoofedBearerToken = useAdminAuthStore((s) => s.spoofedBearerToken);
  const spoofedSessionId = useAdminAuthStore((s) => s.spoofedSessionId);
  const spoofedSessionLabel = useAdminAuthStore((s) => s.spoofedSessionLabel);
  const setSpoofedTenantId = useAdminAuthStore((s) => s.setSpoofedTenantId);
  const setSpoofedBearerToken = useAdminAuthStore((s) => s.setSpoofedBearerToken);
  const setSpoofedSessionId = useAdminAuthStore((s) => s.setSpoofedSessionId);
  const clearSpoofing = useAdminAuthStore((s) => s.clearSpoofing);

  const spoofStore = useMemo(() => ({
    spoofedTenantId,
    spoofedBearerToken,
    spoofedSessionId,
    spoofedSessionLabel,
    setSpoofedTenantId,
    setSpoofedBearerToken,
    setSpoofedSessionId,
    clearSpoofing,
  }), [spoofedTenantId, spoofedBearerToken, spoofedSessionId, spoofedSessionLabel, setSpoofedTenantId, setSpoofedBearerToken, setSpoofedSessionId, clearSpoofing]);

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin' || location.pathname === '/admin/';
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigation = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(path);
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <Sidebar>
      <Sidebar.Header title="Origami Admin" />

      <div className="px-3 py-2 border-b border-secondary-200">
        <TenantSpoofDropdown store={spoofStore} apiClient={adminApiClient} queryKeyPrefix="admin" />
      </div>

      <Sidebar.Section title="Main">
        <Sidebar.Item
          icon={<Icon icon={LayoutDashboard} size="sm" color="inherit" />}
          label="Dashboard"
          href="/admin"
          active={isActive('/admin')}
          onClick={handleNavigation('/admin')}
        />
        <Sidebar.Item
          icon={<Icon icon={Package} size="sm" color="inherit" />}
          label="Modules"
          href="/admin/modules"
          active={isActive('/admin/modules')}
          onClick={handleNavigation('/admin/modules')}
        />
        <Sidebar.Item
          icon={<Icon icon={Building} size="sm" color="inherit" />}
          label="Tenants"
          href="/admin/tenants"
          active={isActive('/admin/tenants')}
          onClick={handleNavigation('/admin/tenants')}
        />
        <Sidebar.Item
          icon={<Icon icon={Users} size="sm" color="inherit" />}
          label="Users"
          href="/admin/users"
          active={isActive('/admin/users')}
          onClick={handleNavigation('/admin/users')}
        />
        <Sidebar.Item
          icon={<Icon icon={Settings2} size="sm" color="inherit" />}
          label="Components"
          href="/admin/components"
          active={isActive('/admin/components')}
          onClick={handleNavigation('/admin/components')}
        />
      </Sidebar.Section>

      <Sidebar.Section title="Workflows">
        <Sidebar.Item
          icon={<Icon icon={GitBranch} size="sm" color="inherit" />}
          label="Definitions"
          href="/admin/workflows"
          active={isActive('/admin/workflows') && !location.pathname.startsWith('/admin/workflows/schedules') && !location.pathname.startsWith('/admin/workflows/executions')}
          onClick={handleNavigation('/admin/workflows')}
        />
        <Sidebar.Item
          icon={<Icon icon={Play} size="sm" color="inherit" />}
          label="Executions"
          href="/admin/workflows/executions"
          active={isActive('/admin/workflows/executions')}
          onClick={handleNavigation('/admin/workflows/executions')}
        />
        <Sidebar.Item
          icon={<Icon icon={Clock} size="sm" color="inherit" />}
          label="Schedules"
          href="/admin/workflows/schedules"
          active={isActive('/admin/workflows/schedules')}
          onClick={handleNavigation('/admin/workflows/schedules')}
        />
      </Sidebar.Section>

      <Sidebar.Section title="ERP">
        <Sidebar.Item
          icon={<Icon icon={Database} size="sm" color="inherit" />}
          label="Registries"
          href="/admin/erp/registries"
          active={isActive('/admin/erp/registries')}
          onClick={handleNavigation('/admin/erp/registries')}
        />
        <Sidebar.Item
          icon={<Icon icon={FileText} size="sm" color="inherit" />}
          label="Request Logs"
          href="/admin/erp/request-logs"
          active={isActive('/admin/erp/request-logs')}
          onClick={handleNavigation('/admin/erp/request-logs')}
        />
      </Sidebar.Section>

      <Sidebar.Section title="Observability">
        <Sidebar.Item
          icon={<Icon icon={Activity} size="sm" color="inherit" />}
          label="Traces"
          href="/admin/observability/traces"
          active={isActive('/admin/observability/traces')}
          onClick={handleNavigation('/admin/observability/traces')}
        />
      </Sidebar.Section>

      <Sidebar.Footer>
        <div className="px-2 py-2">
          <HStack gap={2} align="center" justify="between">
            {user !== null ? (
              <Tooltip
                content={<SessionInfoPopup user={user} activeTenantId={activeTenantId} />}
                position="right"
                className="!max-w-[440px] shadow-xl border border-secondary-700"
              >
                <Text size="sm" color="muted" truncate className="cursor-help">
                  {user.name}
                </Text>
              </Tooltip>
            ) : (
              <Text size="sm" color="muted" truncate>
                Admin
              </Text>
            )}
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Icon icon={LogOut} size="xs" color="inherit" />}
              onClick={handleLogout}
              aria-label="Logout"
            />
          </HStack>
        </div>
      </Sidebar.Footer>
    </Sidebar>
  );
}

interface SessionInfoPopupProps {
  readonly user: AdminUser | null;
  readonly activeTenantId: string | null;
}

// Renders the session detail panel shown inside the sidebar user-name tooltip.
// Surfaces email, roles, and the tenant list with an "Active" marker on the
// currently-selected tenant so admins can sanity-check their session at a glance.
function SessionInfoPopup({ user, activeTenantId }: Readonly<SessionInfoPopupProps>) {
  if (user === null) return null;
  // Defensive defaults: the admin-auth types mark these as required, but the
  // sidebar may be rendered before /me has hydrated (or in tests with partial
  // fixtures) and we prefer a tooltip that degrades gracefully over a crash.
  const roles = user.roles ?? [];
  const tenants = user.tenants ?? [];
  return (
    <VStack gap={3} className="min-w-[280px] max-w-[360px] text-left">
      <VStack gap={1}>
        <Text className="text-white text-sm font-semibold">{user.name}</Text>
        {user.email && <Text className="text-white text-xs opacity-80">{user.email}</Text>}
      </VStack>

      <VStack gap={1}>
        <Text className="text-white text-[11px] font-semibold uppercase tracking-wide opacity-80">Roles</Text>
        {roles.length === 0 ? (
          <Text className="text-white text-xs italic opacity-60">None</Text>
        ) : (
          <HStack gap={1} wrap>
            {roles.map((role) => (
              <Badge key={role} variant="info" size="sm">{role}</Badge>
            ))}
          </HStack>
        )}
      </VStack>

      <VStack gap={1}>
        <Text className="text-white text-[11px] font-semibold uppercase tracking-wide opacity-80">Tenants</Text>
        {tenants.length === 0 ? (
          <Text className="text-white text-xs italic opacity-60">No tenants</Text>
        ) : (
          <VStack gap={2}>
            {tenants.map((tenant) => {
              const isActive = String(tenant.id) === (activeTenantId ?? '');
              return (
                <VStack key={tenant.id} gap={1}>
                  <HStack gap={1} align="center" wrap>
                    <Text className="text-white text-xs font-medium">{tenant.name}</Text>
                    {tenant.role && (
                      <Badge variant="default" size="sm">{tenant.role}</Badge>
                    )}
                    {isActive && (
                      <Badge variant="success" size="sm">Active</Badge>
                    )}
                  </HStack>
                  <Text className="text-white text-[11px] font-mono opacity-70">
                    {String(tenant.id)}
                  </Text>
                </VStack>
              );
            })}
          </VStack>
        )}
      </VStack>
    </VStack>
  );
}
